import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/app-skeleton';
import { cn } from '@/lib/ui/utils';

/** Border radius preset — opt in where nested chrome sits inside `rounded-card` panels. */
export type DataCellCornerRadius = 'card' | 'panelInset';
const CORNER_RADIUS_CLASS: Record<DataCellCornerRadius, string> = {
  card: 'rounded-card',
  /** `rounded-panel-inset` — softer nested corners inside `rounded-card` panels (see globals.css). */
  panelInset: 'rounded-panel-inset'
};
interface DataCellProps {
  /** Optional leading icon. When omitted the cell renders in vertical mode
   *  (label above value, no truncate) — used for paragraph-style content
   *  like the market rules card. */
  icon?: ReactNode | undefined;
  label: string;
  value: ReactNode;
  /** Override the icon colour. Defaults to `text-foreground-tertiary`. (Was `-disabled` pre-2026-05-09 retier.) */
  iconClassName?: string;
  /** Corner preset — use `panelInset` for chips inside a `rounded-card` panel (see `rounded-panel-inset` in `globals.css`). */
  cornerRadius?: DataCellCornerRadius | undefined;
  /** Optional trailing action rendered at the right edge (e.g. a how-it-works info button). */
  action?: ReactNode | undefined;
  className?: string;
}
export function DataCell(t0) {
  const $ = _c(34);
  const {
    icon,
    label,
    value,
    iconClassName,
    cornerRadius: t1,
    action,
    className
  } = t0;
  const cornerRadius = t1 === undefined ? "card" : t1;
  const hasIcon = icon !== undefined && icon !== null;
  const hasAction = action !== undefined && action !== null;
  const t2 = hasIcon ? "flex items-center gap-3" : "flex flex-col gap-1";
  const t3 = hasAction && !hasIcon && "relative";
  const t4 = CORNER_RADIUS_CLASS[cornerRadius];
  let t5;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = cn("border border-border-primary bg-background-primary px-4 py-3 dark:border-border-primary", t2, t3, t4, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== hasIcon || $[6] !== icon || $[7] !== iconClassName) {
    t6 = hasIcon && <span aria-hidden={true} className={cn("flex size-6 shrink-0 items-center justify-center", iconClassName ?? "text-foreground-tertiary")}>{icon}</span>;
    $[5] = hasIcon;
    $[6] = icon;
    $[7] = iconClassName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = hasIcon && "min-w-0";
  let t8;
  if ($[9] !== t7) {
    t8 = cn("flex flex-col gap-y-0", t7);
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = hasIcon && "truncate";
  let t10;
  if ($[11] !== t9) {
    t10 = cn("text-caption text-foreground-tertiary", t9);
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== label || $[14] !== t10) {
    t11 = <span className={t10}>{label}</span>;
    $[13] = label;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = hasIcon ? "truncate text-body-lg" : "text-body";
  let t13;
  if ($[16] !== t12) {
    t13 = cn("font-medium text-foreground-primary", t12);
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t13 || $[19] !== value) {
    t14 = <span className={t13}>{value}</span>;
    $[18] = t13;
    $[19] = value;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t14 || $[23] !== t8) {
    t15 = <div className={t8}>{t11}{t14}</div>;
    $[21] = t11;
    $[22] = t14;
    $[23] = t8;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== action || $[26] !== hasAction || $[27] !== hasIcon) {
    t16 = hasAction ? <div className={cn("shrink-0", hasIcon ? "ml-auto" : "absolute right-2 top-2")}>{action}</div> : null;
    $[25] = action;
    $[26] = hasAction;
    $[27] = hasIcon;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16 || $[31] !== t5 || $[32] !== t6) {
    t17 = <div className={t5}>{t6}{t15}{t16}</div>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t5;
    $[32] = t6;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}
interface DataCellSkeletonProps {
  cornerRadius?: DataCellCornerRadius | undefined;
  className?: string;
}

/** Placeholder matching {@link DataCell} chrome for loading layouts (e.g. proposal detail). */
export function DataCellSkeleton(t0) {
  const $ = _c(7);
  const {
    cornerRadius: t1,
    className
  } = t0;
  const cornerRadius = t1 === undefined ? "card" : t1;
  const t2 = CORNER_RADIUS_CLASS[cornerRadius];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("flex items-center gap-3 border border-border-primary bg-background-primary px-4 py-3 dark:border-border-primary", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Skeleton className="size-6 shrink-0 rounded-item" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex min-w-0 flex-1 flex-col gap-y-0"><Skeleton className="h-3 w-20 max-w-full" /><Skeleton className="h-5 w-28 max-w-full" /></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3) {
    t6 = <div aria-hidden={true} className={t3}>{t4}{t5}</div>;
    $[5] = t3;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}